import { inject, Injectable } from '@angular/core';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ResultsUtilsService } from '@app/modules/reports/services/results-utils.service';
import { ResultsService } from '@app/modules/reports/services/results.service';
import {
  CreateReportGenerationItemAction,
  FinishReportGenerationItemAction,
} from '@app/modules/reports/store/report-generation.actions';
import { GetReportAction } from '@app/modules/reports/store/reports.actions';
import {
  ArchiveResultAction,
  DeleteResultAction,
  GenerateReportAction,
  GetResultAction,
  GetResultsListAction,
  ListXlsVersionsAction,
  ResetResultsPagesAction,
  SetResultsAction,
} from '@app/modules/reports/store/results.actions';
import {
  ImporterInfo,
  ImportType,
  Result,
  ResultRisk,
  ResultStatus,
} from '@app/modules/reports/types/result.types';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { Util } from '@core/utils/core.util';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isEqual } from 'lodash';
import { EMPTY, forkJoin, of, switchMap } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';

const RESULTS_STATE_NAME = 'results';

export interface ResultsStateModel {
  results: { [id: string]: Result };
  pages?: {
    [key: string]: {
      items: string[];
      continuationToken: string;
    };
  };
  importTypes?: ImportType[];
  currentPageSettings: StatePageSettings<ResultsFilter>;
  totalCount?: number;
}

@State<ResultsStateModel>({
  name: RESULTS_STATE_NAME,
  defaults: {
    results: {},
    importTypes: [],
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class ResultsState {
  private reportsUtilsService = inject(ReportsUtilsService);
  private resultsService = inject(ResultsService);
  private resultsUtilsService = inject(ResultsUtilsService);

  @Selector()
  static getImporterInfo(state: ResultsStateModel) {
    return (v: string) => {
      const importType = state.importTypes?.find((type) =>
        type.versions.some((ver) => ver.version === v)
      );
      if (!importType) {
        return null;
      }
      const version = importType.versions.find((ver) => ver.version === v);
      return {
        type: importType,
        version: version,
      } as ImporterInfo;
    };
  }

  @Selector()
  static getImportTypes(state: ResultsStateModel) {
    return state.importTypes.map((importType) => importType.type);
  }

  @Selector()
  static getFullImportTypes(state: ResultsStateModel) {
    return state.importTypes;
  }

  @Selector()
  static getImportType(state: ResultsStateModel) {
    return (type: string) =>
      state.importTypes.find((importType) => importType.type === type);
  }

  @Selector()
  static getResult(state: ResultsStateModel) {
    return (resultId: string) => state.results[resultId];
  }

  @Selector()
  static getResultsByIds(state: ResultsStateModel) {
    return (resultIds: string[]) =>
      resultIds
        ?.map((resultId) => state.results[resultId])
        .filter((result) => !!result);
  }

  @Selector()
  static getCurrentPageIds(state: ResultsStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (
      Object.keys(pages).length === 0 ||
      pageIndex > Object.keys(pages).length ||
      pageIndex < 0
    ) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector([
    ResultsState.getCurrentPageIds,
    ResultsState.getResultsByIds,
    ResultsState.getImporterInfo,
  ])
  static getCurrentPageResults(
    currentPageIds: string[],
    resultsByIds: (resultIds: string[]) => Result[],
    getImporterInfo: (v: string) => { type: any; version: any }
  ) {
    const results = resultsByIds(currentPageIds);
    return results.map((result) => ({
      ...result,
      importerInfo: getImporterInfo(result.dataVersion),
    }));
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: ResultsStateModel): Pagination<ResultsFilter> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Object.keys(pages).length,
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(GetResultAction)
  getResultAction(
    { getState, patchState }: StateContext<ResultsStateModel>,
    { resultId }: GetResultAction
  ) {
    return this.resultsService.getResult(resultId).pipe(
      tap((result) => {
        patchState({
          results: {
            ...getState().results,
            [result.id]: result,
          },
        });
      })
    );
  }

  @Action(ArchiveResultAction)
  archiveResultAction(
    { dispatch }: StateContext<ResultsStateModel>,
    { resultId, reason }: ArchiveResultAction
  ) {
    return this.resultsService.archiveResult(resultId, reason).pipe(
      delay(300),
      switchMap(() => dispatch(new GetResultAction(resultId)))
    );
  }

  @Action(DeleteResultAction)
  deleteResultAction(
    { getState, patchState }: StateContext<ResultsStateModel>,
    { resultId }: DeleteResultAction
  ) {
    return this.resultsService.deleteResult(resultId).pipe(
      tap(() =>
        patchState({
          results: { ...getState().results, [resultId]: null },
        })
      )
    );
  }

  @Action(ListXlsVersionsAction)
  listXlsVersionsAction(
    { patchState }: StateContext<ResultsStateModel>,
    {}: ListXlsVersionsAction
  ) {
    return this.resultsService.listXlsVersions().pipe(
      tap((importTypes) =>
        patchState({
          importTypes,
        })
      )
    );
  }

  @Action(ResetResultsPagesAction)
  resetResultsListAction(
    { patchState, getState, dispatch }: StateContext<ResultsStateModel>,
    {}: ResetResultsPagesAction
  ) {
    patchState({ pages: {} });
    return dispatch(
      new GetResultsListAction(
        { ...getState().currentPageSettings.filterSettings },
        {
          pagingTop: getState().currentPageSettings.sortSettings.pageSize,
          orderDirection:
            getState().currentPageSettings.sortSettings.sortDirection,
          orderBy: getState().currentPageSettings.sortSettings.orderBy,
        },
        1,
        true
      )
    );
  }

  @Action(GenerateReportAction)
  generateReportAction(
    { dispatch, getState }: StateContext<ResultsStateModel>,
    { generationId, resultId }: GenerateReportAction
  ) {
    dispatch(
      new CreateReportGenerationItemAction(
        generationId,
        resultId,
        getState().results[resultId].kitId
      )
    );
    this.resultsService
      .generateReport(resultId)
      .pipe(
        delay(500),
        catchError((err) => of({ error: err.error.Detail }))
      )
      .subscribe((data) => {
        dispatch(new FinishReportGenerationItemAction(generationId, data));
        if ((data as any)?.id) {
          dispatch(new GetReportAction((data as any).id));
        }
      });
  }

  @Action(SetResultsAction)
  setResultsAction(
    { getState, patchState }: StateContext<ResultsStateModel>,
    { results }: SetResultsAction
  ) {
    return patchState({
      ...getState(),
      results: {
        ...getState().results,
        ...results.reduce<{ [id: string]: Result }>((acc, result) => {
          acc[result.id] = result;
          return acc;
        }, {}),
      },
    });
  }

  @Action(GetResultsListAction)
  getResultsListAction(
    { getState, patchState, dispatch }: StateContext<ResultsStateModel>,
    { filters, headers, pageToLoad = 1, reload = false }: GetResultsListAction
  ) {
    // Appends objects are added, in case of separate state @Action calls, as they would otherwise override each other!
    const resultsAppends = {};
    const resultsList = { ...getState().results };

    const state = getState();
    let pages = { ...state.pages };
    const currentPageSettings = {
      ...state.currentPageSettings,
      sortSettings: { ...state.currentPageSettings.sortSettings },
      filterSettings: { ...state.currentPageSettings.filterSettings },
    };
    let totalCount = state.totalCount;

    let actualPageToLoad = pageToLoad ? pageToLoad - 1 : undefined;

    // Update pagination in case of listing pages.
    if (actualPageToLoad !== undefined) {
      currentPageSettings.pageIndex = actualPageToLoad;

      // Check that filters are same as they were for previous page.
      if (
        !isEqual(currentPageSettings.filterSettings, filters) ||
        currentPageSettings.pageSize !== headers.pagingTop ||
        currentPageSettings.sortSettings.sortDirection !==
          headers.orderDirection ||
        currentPageSettings.sortSettings.orderBy !== headers.orderBy
      ) {
        // If filters are different as they were -> update filters, reset pages object and go back to page 1.
        currentPageSettings.pageSize = headers.pagingTop;
        currentPageSettings.sortSettings.sortDirection = headers.orderDirection;
        currentPageSettings.sortSettings.orderBy = headers.orderBy;
        currentPageSettings.sortSettings.pageSize = headers.pagingTop;
        currentPageSettings.filterSettings = filters;

        actualPageToLoad = 0;
        reload = true;
        pages = {};
        currentPageSettings.pageIndex = actualPageToLoad;
      }
    }

    // If reload is not required, check if we already hold service data with selected properties.
    if (filters.ids && !reload) {
      filters.ids = filters.ids.filter((item) => !resultsList[item]);

      // If we filter out all IDs, return early!
      if (!filters.ids.length) {
        return EMPTY;
      }
    }

    if (filters.ids?.length > 100) {
      return forkJoin(
        Util.chunkArray(filters.ids, 100).map((ids) =>
          dispatch(new GetResultsListAction({ ids }, headers, null, reload))
        )
      );
    }

    // Get selected page continuation token, if not first page
    if (actualPageToLoad > 0) {
      headers.continuationToken =
        pages[actualPageToLoad - 1]?.continuationToken;
    }

    //If not listing page, page doesnt exist or reload is demanded -> call API
    if (actualPageToLoad === undefined || !pages[actualPageToLoad] || reload) {
      return this.resultsService
        .listResults(this.resultsUtilsService.buildHttpParams(filters), headers)
        .pipe(
          tap((response) => {
            response.items.forEach((result) => {
              resultsAppends[result.id] = result;
            });

            // Add/update pages
            if (actualPageToLoad !== undefined) {
              totalCount = response.totalCount;
              pages[actualPageToLoad] = {
                items: response.items.map((result) => result.id),
                continuationToken: response.continuationToken,
              };
            }
          }),
          tap(() => {
            // get latest state again as state might be patched in some other @Action
            const oldState = getState();
            patchState({
              results: { ...oldState.results, ...resultsAppends },
              pages,
              totalCount,
              currentPageSettings,
            });
          }),
          catchError(() => {
            patchState({});
            return of(null);
          })
        );
    } else {
      // If page exists -> update state so pagination is updated.
      patchState({ currentPageSettings });
      return EMPTY;
    }
  }
}

export interface ResultsFilter extends ApiFilters {
  ids?: string[];
  kitIdToken?: string;
  kitIds?: string[];
  laboratories?: string[];
  dataVersions?: string[];
  signeeId?: string;
  detectedRisk?: ResultRisk;
  lastStatuses?: ResultStatus[];
  lastStatus?: ResultStatus;
  lastStatusChangedFrom?: string;
  lastStatusChangedTo?: string;
  status?: ResultStatus;
  statusChangedFrom?: string;
  statusChangedTo?: string;
  generatedFrom?: string;
  generatedTo?: string;
  failedFrom?: string;
  failedTo?: string;
  reportedFrom?: string;
  reportedTo?: string;
  archivedFrom?: string;
  archivedTo?: string;
}
